/**
 * useFABContext — Context-aware override for FAB slot 0.
 *
 * Returns the highest-priority applicable context override, or null.
 * Priority order:
 *   1. Safety timer active
 *   2. Sub: active chastity device
 *   3. Dom: sub missed check-in         [stub — no hook exists yet]
 *   4. Sub: overdue task (count > 0)    [stub — no hook exists yet]
 *   5. Dom: pending extension request   [stub — no hook exists yet]
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { safetyCheckinAPI } from '../services/api/safetyCheckin';
import { chastityAPI } from '../services/api';
import { unwrapResponse } from '../utils/unwrapResponse';
import { useIdentity } from './useIdentity';
import { useOrganization } from '../contexts/OrganizationContext';

export interface FABContextOverride {
  slot: 0;
  actionKey: string;
  label: string;
  icon: React.ReactNode;
  reason: string;
  badge?: string;
  tinted: boolean;
}

// ─── Shared icons (inline) ────────────────────────────────────────────────────

const ClockIcon: React.ReactNode = React.createElement(
  'svg',
  {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  },
  React.createElement('circle', { cx: '12', cy: '12', r: '9' }),
  React.createElement('path', { d: 'M12 7v5l3 2' })
);

const PulseIcon: React.ReactNode = React.createElement(
  'svg',
  {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  },
  React.createElement('path', { d: 'M3 12h3l2.5-6 4 12 2.5-6h6' })
);

const ChecklistIcon: React.ReactNode = React.createElement(
  'svg',
  {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  },
  React.createElement('path', { d: 'M3 5l2 2 3-3' }),
  React.createElement('path', { d: 'M3 12l2 2 3-3' }),
  React.createElement('path', { d: 'M3 19l2 2 3-3' }),
  React.createElement('line', { x1: '12', y1: '5', x2: '21', y2: '5' }),
  React.createElement('line', { x1: '12', y1: '12', x2: '21', y2: '12' }),
  React.createElement('line', { x1: '12', y1: '19', x2: '21', y2: '19' })
);

const CheckOnSubIcon: React.ReactNode = React.createElement(
  'svg',
  {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  },
  React.createElement('circle', { cx: '10', cy: '8', r: '3.5' }),
  React.createElement('path', { d: 'M3.5 20c0-3.6 2.9-6.5 6.5-6.5 1.1 0 2.1.3 3 .7' }),
  React.createElement('circle', { cx: '17', cy: '16', r: '3' }),
  React.createElement('path', { d: 'M19.2 18.2L22 21' })
);

const ReviewIcon: React.ReactNode = React.createElement(
  'svg',
  {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  },
  React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
  React.createElement('circle', { cx: '12', cy: '12', r: '3' })
);

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useFABContext(): FABContextOverride | null {
  const { isDominant, isSubmissive } = useIdentity();
  const { organization } = useOrganization();
  const orgId = organization?.id ?? '';

  // --- Priority 1: Safety timer active ---
  const safetyTimerQuery = useQuery({
    queryKey: ['fab-context', 'safety-timer', orgId],
    queryFn: async () => {
      const resp = await safetyCheckinAPI.getActive();
      const checkin = unwrapResponse<{ status: string } | null>(resp.data);
      return checkin?.status === 'active' ? checkin : null;
    },
    enabled: !!orgId,
    staleTime: 30_000,
    retry: 1,
  });

  // --- Priority 2: Sub — active chastity device ---
  // Only query when the user is submissive (don't fire for doms)
  const chastityQuery = useQuery({
    queryKey: ['fab-context', 'chastity-active', orgId],
    queryFn: async () => {
      const resp = await chastityAPI.getActiveLock();
      // chastityAPI.getActiveLock returns AxiosResponse<{ data: ChastityLock | null }>
      const lock = unwrapResponse<{ status?: string } | null>(resp.data);
      return lock && lock.status === 'active' ? lock : null;
    },
    enabled: isSubmissive,
    staleTime: 60_000,
    retry: 1,
  });

  // --- Priority 3: Dom — sub missed check-in [STUB] ---
  // TODO: No `usePartnerCheckinStatus` hook exists. Would need:
  //   GET /organizations/:orgId/members/:partnerId/checkins — filtered by last_checkin_at
  //   and a threshold (e.g. > 24h since last check-in). Wire when that endpoint is implemented.
  const subMissedCheckin: boolean = false;

  // --- Priority 4: Sub — overdue task count [STUB] ---
  // TODO: No dedicated overdue-count endpoint exists. Would need:
  //   GET /tasks?assignedToMe=true&overdue=true&limit=1 with a total-count field,
  //   or a dedicated GET /tasks/overdue-count endpoint.
  const overdueTaskCount: number = 0;

  // --- Priority 5: Dom — pending extension request [STUB] ---
  // TODO: No `usePendingExtensionRequests` hook exists. Would need:
  //   GET /tasks/extension-requests?status=pending&limit=1 or similar.
  //   Wire when that endpoint is implemented.
  const hasPendingExtension: boolean = false;

  // ─── Priority resolution ────────────────────────────────────────────────────

  // 1. Safety timer
  if (safetyTimerQuery.data) {
    return {
      slot: 0,
      actionKey: 'safety_timer',
      label: 'Safety Timer',
      icon: ClockIcon,
      reason: 'Safety timer running',
      tinted: true,
    };
  }

  // 2. Sub: active chastity device
  if (isSubmissive && chastityQuery.data) {
    return {
      slot: 0,
      actionKey: 'health_check',
      label: 'Health Check',
      icon: PulseIcon,
      reason: 'Active chastity device',
      tinted: true,
    };
  }

  // 3. Dom: sub missed check-in [stub]
  if (isDominant && subMissedCheckin) {
    return {
      slot: 0,
      actionKey: 'check_on_sub',
      label: 'Check on Sub',
      icon: CheckOnSubIcon,
      reason: 'Partner missed check-in',
      tinted: true,
    };
  }

  // 4. Sub: overdue task [stub]
  if (isSubmissive && overdueTaskCount > 0) {
    return {
      slot: 0,
      actionKey: 'view_overdue',
      label: 'View Overdue',
      icon: ChecklistIcon,
      reason: 'Overdue task',
      badge: overdueTaskCount > 3 ? '3+' : String(overdueTaskCount),
      tinted: true,
    };
  }

  // 5. Dom: pending extension request [stub]
  if (isDominant && hasPendingExtension) {
    return {
      slot: 0,
      actionKey: 'review_extension',
      label: 'Review Extension',
      icon: ReviewIcon,
      reason: 'Pending extension request',
      tinted: true,
    };
  }

  return null;
}
